/**
 * The selected PLANT — the part of a site an operator is looking at.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THERE IS A LEVEL BELOW THE SITE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Jamshoro's as-built drawing is five independent plants, not one: 459.03 kW on four
 * inverters, then 102.96 on two, 77.22 on two, 185.76 on one and 45.76 on one. Each combines
 * its own inverters on its own AC bus and meters separately, and nothing in the drawing ties
 * the five together. We modelled all ten inverters as one flat set, so a real, physical
 * division of the plant was invisible on every screen.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE SOURCE OF TRUTH MOVED — 2026-09-18
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * This file used to say, at length, that a plant "IS DERIVED, NOT STORED … there is no table,
 * no tag and nothing that can disagree with the graph", and that both live sites had no plants
 * at all. **All three statements are now false**, and the stale comment is most of why the bug
 * below survived a week.
 *
 * Migration 0048 made `plants(id, organisation_id, site_id, name, derived_from_node_id)` a real
 * table and put `plant_id` on `site_devices`. Jamshoro carries Plant 1–5 as rows today, with
 * four, two, two, one and one device filed into them; the weather station and the HESCO meter
 * are site-wide and carry `NULL`.
 *
 * Meanwhile this provider kept calling `derivePlantGroups`, so the header served
 * `g:BUS-P1 … g:BUS-P5` — **derived topology node ids**. The access editor and the site hub read
 * the table. So "Plant 1" in the header and "Plant 1" in somebody's site grant were unrelated
 * identifiers that happened to share a label, and `nextNodeId()` recycles `BUS-n`, which is the
 * precise reason a derived id was ruled out as a durable target in the first place.
 *
 * It now reads the table. `id` is a `plants.id` uuid, so the one identifier means one thing
 * everywhere: the header, a grant, and the device a reading came from.
 *
 * `derivePlantGroups` IS NOT DEAD — see `lib/plant-groups.ts`. It is the PROPOSAL engine: it
 * reads the graph and suggests a division for a human to accept into the table, which is why
 * `plants.derived_from_node_id` exists to record where an accepted one came from. What moved is
 * the header filter's source of truth, not the derivation.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE PROPERTIES THAT STILL HOLD, RESTATED AGAINST THE TABLE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * 1. MEMBERSHIP IS A RECORDED FACT, and therefore a human's to own. A device moves between
 *    plants when somebody files it on the site hub's Devices tab, not when an engineer redraws
 *    the single line. That is the trade the table bought: the graph can no longer silently
 *    move who sees what, and in exchange re-filing is an explicit act that can be got wrong.
 *    `provenance: 'recorded'` on every group says which of the two a reader is looking at.
 *
 * 2. `deviceIds: null` MEANS DO NOT NARROW, unchanged and still the whole contract of that
 *    field. Every consumer must branch on null rather than on length. An empty array would
 *    mean "narrow to nothing" and would blank the screen, which is the failure mode a filter
 *    added to a console usually ships with. `modules/sites/pages/plant-filter.ts` is where
 *    that rule is enforced once for every screen.
 *
 * 3. THE SELECTION RESETS WHEN THE SITE CHANGES. Still true and still necessary, for a better
 *    reason than before: a `plants.id` is globally unique, so carrying one across a site change
 *    would not collide — it would simply match nothing, and by rule 2 that reads as a site with
 *    no devices rather than as a mistake. Resetting says so immediately.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE TABLE OWNS IDENTITY AND MEMBERSHIP; THE GRAPH STILL OWNS SHAPE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `SldDiagram` dims the parts of the single line outside the selected plant, and that is keyed
 * on NODE ids — so a plant needs a node set as well as a device set.
 *
 * The first version of this took only the nodes that SPEAK for the plant's devices, and it was
 * visibly wrong: a busbar carries no device of its own, and neither do the strings or the
 * conductors, so selecting Plant 1 lit four scattered inverter cards and dimmed the bus they
 * combine on. That is not a plant, it is four machines. `derived_from_node_id` would have
 * rescued the bus, and it is NULL on all five of Jamshoro's rows — the API that created them
 * never recorded one, which is exactly the kind of "usually populated" field a diagram must not
 * depend on.
 *
 * So the shape comes from the same upstream walk it always did. `derivePlantGroups` runs over
 * the document, and each RECORDED plant adopts the node set of the derived group its devices
 * actually land in — by anchor when one was recorded, by greatest device overlap otherwise.
 * Identity and membership stay the table's; only the extent on the canvas is the graph's, which
 * is the one question the table cannot answer and the graph can.
 *
 * A plant with no match keeps the device-bearing nodes alone. That happens when the plant is
 * recorded but not drawn, and highlighting only what is provably in it is the honest answer —
 * better than borrowing a neighbouring bus's subgraph because it was the closest thing.
 *
 * Costs one request beyond the topology `SLD Studio` already holds: the device inventory, which
 * `useAllDevices` follows to the END of its cursor rather than guessing a page size. That is
 * GUIDELINES §11's second corollary and it is not theoretical — `SiteAccessSection` asked for
 * `limit: 500` against a cap of 200 on 2026-09-18, got a silent 422, and rendered every plant
 * with no devices in it. `inventoryTruncated` below is the cap being READ.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAllDevices } from '@/lib/api/onboarding';
import { usePlants } from '@/lib/api/scope';
import { useTopology } from '@/lib/api/topology';
import {
  derivePlantGroups,
  devicesOf,
  groupingFor,
  showsGroupFilter,
  type PlantGroup,
  type PlantGrouping,
} from '@/lib/plant-groups';
import { useSiteContext } from './site-context-value';
import { PlantContext, type PlantContextValue } from './plant-context-value';

export function PlantProvider({ children }: { children: ReactNode }): ReactNode {
  const { selected: site } = useSiteContext();
  const siteId = site?.id ?? null;

  /* The record. One row per plant, with the name a human typed. */
  const plants = usePlants(siteId);
  /*
   * Which devices are filed where. EVERY status, because membership is what the table says and
   * a pending device filed into a plant is still filed there — it simply has no readings to
   * narrow yet. Filtering to `allowed` here would put it in no plant, and by `plantAdmits`'
   * site-wide clause that would show it under every plant instead of its own.
   */
  const inventory = useAllDevices(siteId);
  /* Only for the node mapping below, and it is the same cache entry SLD Studio holds at
     `staleTime: Infinity` — so this costs no request of its own. */
  const topology = useTopology(siteId);

  const [selectedId, setSelectedId] = useState<string | null>(null);

  /* Rule 3. Keyed on the site id rather than on the document, so re-fetching the same site's
     topology — which SLD Studio does after every save — does not drop the operator's
     selection mid-investigation. */
  useEffect(() => {
    setSelectedId(null);
  }, [siteId]);

  const grouping = useMemo<PlantGrouping>(() => {
    const rows = plants.data?.data ?? [];
    const devices = inventory.data?.data ?? [];
    const nodes = topology.data?.doc.nodes ?? [];

    /* The graph's own division, used ONLY for shape — see the header. */
    const drawn = derivePlantGroups(nodes, site?.archetype ?? null);

    const groups: PlantGroup[] = rows.map((row) => {
      const deviceIds = devices.filter((d) => d.plantId === row.id).map((d) => d.id);
      const own = new Set(deviceIds);

      /* By anchor when it was recorded — exact. Otherwise the drawn group this plant's devices
         mostly land in, which is how a plant recorded after the drawing finds its own extent. */
      const byAnchor =
        row.derivedFromNodeId === null
          ? undefined
          : drawn.groups.find((g) => g.anchorNodeId === row.derivedFromNodeId);
      const byOverlap = drawn.groups
        .map((g) => ({ g, n: g.deviceIds.filter((id) => own.has(id)).length }))
        .filter((x) => x.n > 0)
        .sort((a, b) => b.n - a.n)[0]?.g;
      const shape = byAnchor ?? byOverlap;

      const nodeIds =
        shape !== undefined
          ? [...shape.nodeIds].sort()
          : /* Recorded but not drawn: only what is provably in it. */
            nodes
              .filter((n) => devicesOf(n).some((id) => own.has(id)))
              .map((n) => n.id)
              .sort();

      return {
        id: row.id,
        label: row.name,
        /* What it was accepted from, else the bus its devices were found on. Display and
           matching only — identity is `id`, which is the whole point of the move to a table. */
        anchorNodeId: row.derivedFromNodeId ?? (shape?.anchorNodeId ?? ''),
        nodeIds,
        deviceIds: [...deviceIds].sort((a, b) => a - b),
        /* A table row is a recorded fact, which is exactly what this field distinguishes. */
        provenance: 'recorded' as const,
        why:
          `${deviceIds.length} device${deviceIds.length === 1 ? '' : 's'} filed into ` +
          `${row.name} on the site's Devices tab` +
          (row.derivedFromNodeId === null ? '' : `, accepted from ${row.derivedFromNodeId}`),
      };
    });

    /*
     * Site-wide equipment — the meter, the weather station — is what belongs to NO plant, and
     * that is read off `plant_id IS NULL` rather than off the graph. The old derivation could
     * only report devices the topology had drawn, which measured empty at Jamshoro and made
     * `plant-filter.ts`'s whole site-wide clause inert; that fix already moved the clause to a
     * union of every plant's devices, and this makes the set itself honest too.
     */
    const ungroupedDeviceIds = devices
      .filter((d) => d.plantId === null)
      .map((d) => d.id)
      .sort((a, b) => a - b);

    const claimed = new Set(groups.flatMap((g) => g.nodeIds));
    return {
      groups,
      ungroupedNodeIds: nodes
        .filter((n) => !claimed.has(n.id))
        .map((n) => n.id)
        .sort(),
      ungroupedDeviceIds,
    };
  }, [plants.data, inventory.data, topology.data, site?.archetype]);

  const value = useMemo<PlantContextValue>(() => {
    const selected = grouping.groups.find((g) => g.id === selectedId) ?? null;
    return {
      grouping,
      spec: groupingFor(site?.archetype ?? null),
      /* One part is not a division of anything, so a single-plant site gets no control and no
         explaining to do — hidden, not disabled, the same rule the industry switcher follows. */
      visible: showsGroupFilter(grouping),
      selectedId: selected?.id ?? null,
      selected,
      /* Set through the callback below, replaced here so the value is stable per render. */
      select: () => {},
      deviceIds: selected ? selected.deviceIds : null,
      /* The record and the inventory both have to arrive before a plant can be offered. */
      loading: plants.isLoading || inventory.isLoading,
      /* The cap, READ. True means a plant's device list is short, which by `plantAdmits`' own
         site-wide clause shows the missing devices under EVERY plant rather than hiding them —
         wrong in the widening direction, which is the safe one, and still worth saying. */
      inventoryTruncated: inventory.data?.truncated === true,
    };
  }, [grouping, selectedId, site?.archetype, plants.isLoading, inventory.isLoading, inventory.data]);

  const select = useCallback((id: string | null) => setSelectedId(id), []);
  const withSelect = useMemo<PlantContextValue>(() => ({ ...value, select }), [value, select]);

  return <PlantContext.Provider value={withSelect}>{children}</PlantContext.Provider>;
}
